import React from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as NoDataSearchIcon } from '../../../../Assets/images/svg/no-data-search-icon.svg';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DISPLAY_DATE_WITH_COMMA = /^(\d{1,2})\s+([A-Za-z]{3,}),\s+(\d{4})$/;
const DISPLAY_DATE_WITH_DASH = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const normalizeMonthToken = (monthValue = '') => {
  const monthPrefix = String(monthValue).trim()
    .slice(0, 3);
  if (!monthPrefix) return '';

  const normalized = monthPrefix.charAt(0).toUpperCase() + monthPrefix.slice(1).toLowerCase();
  return MONTHS.includes(normalized) ? normalized : '';
};

const normalizeCsatDisplayDate = (inputDate) => {
  const raw = String(inputDate ?? '').trim();
  if (!raw) return raw;

  const commaMatch = raw.match(DISPLAY_DATE_WITH_COMMA);
  if (commaMatch) {
    const [, day, month, year] = commaMatch;
    const normalizedMonth = normalizeMonthToken(month);
    return normalizedMonth ? `${String(Number(day)).padStart(2, '0')}-${normalizedMonth}-${year}` : raw;
  }

  const dashedMatch = raw.match(DISPLAY_DATE_WITH_DASH);
  if (dashedMatch) {
    const [, day, month, year] = dashedMatch;
    const normalizedMonth = normalizeMonthToken(month);
    return normalizedMonth ? `${String(Number(day)).padStart(2, '0')}-${normalizedMonth}-${year}` : raw;
  }

  const isoMatch = raw.match(ISO_DATE);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const normalizedMonth = MONTHS[Number(month) - 1];
    return normalizedMonth ? `${day}-${normalizedMonth}-${year}` : raw;
  }

  return raw;
};

const getGaugeNeedle = (percentage = 0) => {
  const clampedPercentage = Math.max(0, Math.min(100, Number(percentage) || 0));
  const centerX = 120.5;
  const centerY = 88;
  const startAngle = 180;
  const sweepAngle = 180;
  const angle = ((startAngle + ((clampedPercentage / 100) * sweepAngle)) * Math.PI) / 180;
  const innerRadius = 64;
  const outerRadius = 92;

  return {
    x1: centerX + (Math.cos(angle) * innerRadius),
    y1: centerY + (Math.sin(angle) * innerRadius),
    x2: centerX + (Math.cos(angle) * outerRadius),
    y2: centerY + (Math.sin(angle) * outerRadius),
  };
};

const getCsatColorClass = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return '';
  if (numericValue <= 3) return 'red';
  if (numericValue > 3 && numericValue <= 4) return 'yellow';
  return 'green';
};

const CsatCard = ({
  hasCsatSummary,
  csatGaugeData,
  csatSummary,
}) => {
  const needle = getGaugeNeedle(csatGaugeData?.percentage);
  const hasFilledArc = Number(csatGaugeData?.percentage) > 0;
  const csatScore = Number(csatGaugeData?.displayScore ?? csatSummary?.score ?? 0);
  // const averageCsatValue = Number(csatSummary?.averageCsat ?? 0);
  const csatScoreColorClass = getCsatColorClass(csatScore);
  // const averageCsatColorClass = getCsatColorClass(averageCsatValue);
  const showEmptyState = !hasCsatSummary || !Number.isFinite(csatScore) || csatScore < 0;

  return (
    <div className={`card csat-card ${showEmptyState ? 'no-data' : 'with-data'}`}>
      <div className="card-header">
        <h3 className="card-title">CSAT</h3>
        <span className="divider" />
      </div>
      <div className="card-content">
        {showEmptyState ? (
          <div className="csat-empty-state">
            <NoDataSearchIcon className="csat-empty-icon" aria-hidden="true" focusable="false" />
            <span className="csat-empty-text">No data to display</span>
          </div>
        ) : (
          <div className="csat-gauge">
            <svg viewBox="0 0 241 118" className="gauge-svg">
              <path
                d="M 42 88 A 78.5 78.5 0 0 1 199 88"
                fill="none"
                stroke="#ECF1F7"
                strokeWidth="18"
                strokeLinecap="round"
                pathLength="220"
              />
              <path
                d="M 42 88 A 78.5 78.5 0 0 1 199 88"
                fill="none"
                stroke="#D8DEFF"
                strokeWidth="18"
                strokeLinecap="butt"
                strokeDasharray="220 220"
                strokeDashoffset={csatGaugeData?.strokeDashoffset || 0}
                pathLength="220"
              />
              {hasFilledArc ? (
                <circle
                  cx="42"
                  cy="88"
                  r="9"
                  fill="#D8DEFF"
                />
              ) : null}
              <line
                x1={needle.x1}
                y1={needle.y1}
                x2={needle.x2}
                y2={needle.y2}
                className="needle-line"
              />
              <text
                x="120.5"
                y="62"
                textAnchor="middle"
                className={[
                  'score-text',
                  csatScoreColorClass,
                ].join(' ').trim()}
              >
                {csatGaugeData?.displayScore || '0.00'}
              </text>
              <text x="120.5" y="82" textAnchor="middle" className="date-text">
                {normalizeCsatDisplayDate(csatSummary?.date ?? '-')}
              </text>
            </svg>
          </div>
        )}

        {showEmptyState ? null : (
          <div>
            {/* <div className="csat-details">
            <div className="csat-detail-row">
              <span className="csat-label">No. of responses received:</span>
              <span className="csat-value">
                {hasCsatSummary
                  ? String(csatSummary?.responsesReceived ?? 0).padStart(2, '0')
                  : '-'}
              </span>
            </div>
            <div className="csat-detail-row">
              <span className="csat-label">Average CSAT</span>
              <span className={[
                'csat-value',
                averageCsatColorClass,
              ].join(' ').trim()}
              >
                {hasCsatSummary
                  ? Number(csatSummary?.averageCsat || 0).toFixed(1)
                  : '-'}
              </span>
            </div>
          </div> */}
          </div>
        )}

      </div>
    </div>
  );
};

export default CsatCard;

CsatCard.propTypes = {
  hasCsatSummary: PropTypes.bool.isRequired,
  csatGaugeData: PropTypes.shape({
    percentage: PropTypes.number,
    strokeDashoffset: PropTypes.number,
    displayScore: PropTypes.string,
  }),
  csatSummary: PropTypes.shape({
    score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    date: PropTypes.string,
    responsesReceived: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    averageCsat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

CsatCard.defaultProps = {
  csatGaugeData: null,
  csatSummary: null,
};
